import React, { useMemo } from 'react';

import style from './index.module.scss';

import clsx from 'clsx';
import { Box, BoxProps, useTheme } from '@mui/material';

export interface StackProps extends BoxProps {
  className?: string;
  divider?: React.ReactNode;
  spacing?: number;
  direction?: 'row' | 'column';
}

export const Stack: React.FC<React.PropsWithChildren<StackProps>> = ({
  className,
  children,
  divider,
  spacing = 0,
  direction = 'row',
  ...boxProps
}) => {
  const theme = useTheme();

  const styles = useMemo(() => {
    const space = theme.spacing(spacing);
    return {
      ...(direction === 'row'
        ? { ...(theme.direction === 'ltr' ? { marginLeft: space } : { marginRight: space }) }
        : {}),
      ...(direction === 'column' ? { marginTop: space } : {}),
    };
  }, [direction, spacing, theme]);
  const Items = useMemo(() => {
    return React.Children.toArray(children).map((element, i) => {
      if (React.isValidElement<{ style?: React.CSSProperties }>(element)) {
        return React.cloneElement(element, {
          ...element.props,
          style: { ...element.props.style, ...(i !== 0 ? styles : {}) },
        });
      }
      return null;
    });
  }, [children, styles]);

  const Divider = useMemo(() => {
    if (React.isValidElement<{ style?: React.CSSProperties }>(divider)) {
      return React.cloneElement(divider, {
        ...divider.props,
        style: { ...divider.props.style, ...styles },
      });
    }
    return null;
  }, [divider, styles]);

  return (
    <Box
      className={clsx(style.root, className)}
      {...boxProps}
      style={{ ...boxProps?.style, flexDirection: direction }}
    >
      {Items.map((Component, i, arr) => (
        <React.Fragment key={i}>
          {Component}
          {arr.length - 1 !== i && Divider}
        </React.Fragment>
      ))}
    </Box>
  );
};
